import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import LoadingIcon from "../../../../../components/LoadingIcon";
import { Toast } from "../../../../../components/Toast";
import { CustomerFeatureFlagsContext } from "../../../../../contexts/CustomerFeatureFlagsContext";
import {
  FILE_EXTENSIONS,
  useFileNaming,
} from "../../../../../hooks/useFileNaming";
import { LATEST, TimePoint } from "../../../../../utils/constants";
import { icons } from "../../../../stack/environment/icons/iconExport";
import {
  baseTimePointOptions,
  createPdfHeader,
  createTrendSortMenuItems,
} from "../../utils/commonWidgetUtils";
import WidgetWrapper from "../../WidgetWrapper";
import SecurityDomainScoreChart from "./SecurityDomainScoreChart";

const SecurityDomainScoreWidget = forwardRef(({ mainTimePoint }, ref) => {
  const colors = useColors();
  const { trackEvent, getHeatmapMatrixHistory } = useAPI();
  const { generatePdfFileName } = useFileNaming();
  const { heatmapDisplayMode } = useContext(CustomerFeatureFlagsContext);

  const contentRef = useRef(null);
  const [baseTimePoint, setBaseTimePoint] = useState(TimePoint.LATEST);
  const [selectedMatrix, setSelectedMatrix] = useState({});

  const {
    data: heatmapMatrixHistory,
    isLoading: isLoadingHeatmapMatrixHistory,
  } = useQuery({
    queryKey: ["heatmapMatrixHistory"],
    queryFn: getHeatmapMatrixHistory,
    select: (response) => response.data,
  });

  useEffect(() => {
    if (
      heatmapMatrixHistory &&
      heatmapMatrixHistory[heatmapDisplayMode] &&
      heatmapMatrixHistory[heatmapDisplayMode][mainTimePoint]
    ) {
      setBaseTimePoint(mainTimePoint);
    }
  }, [mainTimePoint, heatmapDisplayMode]);

  useEffect(() => {
    if (!heatmapMatrixHistory) return;

    const latestData = heatmapMatrixHistory[LATEST];
    if (!latestData) return;

    if (heatmapDisplayMode) {
      const displayModeData = latestData[heatmapDisplayMode];
      if (displayModeData) {
        setSelectedMatrix(displayModeData);
      }
    } else {
      setSelectedMatrix(latestData);
    }
  }, [heatmapMatrixHistory, heatmapDisplayMode]);

  // Calculate domain scores (average coverage per row/domain)
  const domainScores = useMemo(() => {
    if (!selectedMatrix || Object.keys(selectedMatrix).length === 0) {
      return {};
    }

    const scores = {};

    Object.entries(selectedMatrix).forEach(([domain, pillars]) => {
      const pillarCoverages = [];

      Object.entries(pillars).forEach(([pillar, data]) => {
        if (
          data &&
          typeof data.coverage === "number" &&
          data.combined_severity !== 0
        ) {
          pillarCoverages.push(data.coverage);
        }
      });

      if (pillarCoverages.length > 0) {
        const averageCoverage =
          pillarCoverages.reduce((sum, coverage) => sum + coverage, 0) /
          pillarCoverages.length;
        scores[domain] = {
          coverage: Math.round(averageCoverage),
          pillarsCount: pillarCoverages.length,
        };
      }
    });

    return scores;
  }, [selectedMatrix]);

  // Calculate trend data for domain scores
  const calculateDomainTrends = useMemo(() => {
    if (
      !heatmapMatrixHistory ||
      !selectedMatrix ||
      baseTimePoint === TimePoint.LATEST ||
      Object.keys(domainScores).length === 0
    ) {
      return {};
    }

    const baseTimepointData = heatmapMatrixHistory[baseTimePoint];
    const baseMatrix = heatmapDisplayMode
      ? baseTimepointData && baseTimepointData[heatmapDisplayMode]
      : baseTimepointData;

    if (!baseMatrix) return {};

    const trends = {};

    Object.entries(domainScores).forEach(([domain, currentData]) => {
      const baseDomainData = baseMatrix[domain];
      if (!baseDomainData) return;

      // Calculate base domain average coverage
      const basePillarCoverages = [];
      Object.entries(baseDomainData).forEach(([pillar, data]) => {
        if (data && typeof data.coverage === "number") {
          basePillarCoverages.push(data.coverage);
        }
      });

      if (basePillarCoverages.length > 0) {
        const baseAverageCoverage =
          basePillarCoverages.reduce((sum, coverage) => sum + coverage, 0) /
          basePillarCoverages.length;
        const currentCoverage = currentData.coverage;
        const coverageChange =
          currentCoverage - Math.round(baseAverageCoverage);

        trends[domain] = {
          current: currentCoverage,
          previous: Math.round(baseAverageCoverage),
          percentChange: coverageChange,
          isPositive: coverageChange > 0,
        };
      }
    });

    return trends;
  }, [
    heatmapMatrixHistory,
    selectedMatrix,
    baseTimePoint,
    domainScores,
    heatmapDisplayMode,
  ]);

  const handleExportToPdf = useCallback(
    async (isCombinedExport = false) => {
      if (!contentRef.current || isLoadingHeatmapMatrixHistory) return;

      if (!isCombinedExport) {
        trackEvent(
          "User clicked on Export to PDF button in Security Domain Score"
        );
      }

      try {
        if (isCombinedExport) {
          const container = contentRef.current;
          const canvas = await html2canvas(container, {
            scale: 1.5,
            useCORS: true,
            allowTaint: true,
            backgroundColor: colors.common_white,
            logging: false,
            imageTimeout: 0,
            onclone: (clonedDoc, element) => {
              const title = createPdfHeader("Security Domain Score");
              element.insertBefore(title, element.firstChild);

              const exportButton = element.querySelector(
                '[data-export-button="true"]'
              );
              if (exportButton instanceof HTMLElement) {
                exportButton.style.display = "none";
              }
            },
          });

          const widgetContainer = document.createElement("div");
          widgetContainer.style.cssText = `
          background-color: white;
          padding: 24px;
          border: none;
          width: 100%;
          min-height: fit-content;
          page-break-inside: avoid;
        `;

          return new Promise((resolve, reject) => {
            try {
              const img = document.createElement("img");
              const imageData = canvas.toDataURL("image/png", 1.0);

              img.onload = () => {
                resolve(widgetContainer);
              };

              img.onerror = (error) => {
                console.error(
                  "Security Domain Score image failed to load:",
                  error
                );
                resolve(widgetContainer);
              };

              img.src = imageData;
              img.style.cssText = `
              width: 100%;
              height: auto;
              max-width: 100%;
              display: block;
            `;

              widgetContainer.appendChild(img);

              setTimeout(() => {
                resolve(widgetContainer);
              }, 1000);
            } catch (error) {
              console.error(
                "Error creating Security Domain Score image:",
                error
              );
              reject(error);
            }
          });
        } else {
          const container = contentRef.current;
          const canvas = await html2canvas(container, {
            scale: 1.5,
            useCORS: true,
            allowTaint: true,
            backgroundColor: colors.common_white,
            logging: false,
            imageTimeout: 0,
            onclone: (clonedDoc, element) => {
              const title = createPdfHeader("Security Domain Score");
              element.insertBefore(title, element.firstChild);

              const exportButton = element.querySelector(
                '[data-export-button="true"]'
              );
              if (exportButton instanceof HTMLElement) {
                exportButton.style.display = "none";
              }
            },
          });

          const pdf = new jsPDF({
            orientation: "landscape",
            unit: "px",
            format: "a4",
            compress: true,
          });

          const optimizeImageData = (canvas) => {
            return canvas.toDataURL("image/jpeg", 0.7);
          };

          const imgData = optimizeImageData(canvas);
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = pdf.internal.pageSize.getHeight();
          const imgWidth = canvas.width;
          const imgHeight = canvas.height;
          const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
          const imgX = (pdfWidth - imgWidth * ratio) / 2;
          const imgY = (pdfHeight - imgHeight * ratio) / 2;

          pdf.addImage(
            imgData,
            "JPEG",
            imgX,
            imgY,
            imgWidth * ratio,
            imgHeight * ratio,
            undefined,
            "FAST"
          );

          pdf.save(
            generatePdfFileName(
              "Security_Domain_Score",
              null,
              FILE_EXTENSIONS.PDF
            )
          );
        }
      } catch (error) {
        Toast.error(
          "Failed to export Security Domain Score, Please try again later"
        );

        return null;
      }
    },
    [contentRef, isLoadingHeatmapMatrixHistory]
  );

  useImperativeHandle(
    ref,
    () => ({
      exportToPdf: handleExportToPdf,
      widgetId: "security-domain-score",
      hasExportCapability: true,
    }),
    [handleExportToPdf]
  );

  const CalculationInfo = () => {
    return (
      <Box>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          How is the score calculated?
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Score is derived from the heatmap calculation. Each Domain score
          reflects multiple risk factors, including the number of affected
          entities, gap severity, and involvement in attack scenarios. To
          improve a Domain score, prioritize remediating its most severe gaps.
        </Typography>
      </Box>
    );
  };

  const sortMenuItems = createTrendSortMenuItems(
    baseTimePoint,
    setBaseTimePoint,
    heatmapMatrixHistory,
    icons,
    trackEvent,
    "security-domain-score",
    heatmapDisplayMode
  );

  return (
    <WidgetWrapper
      title="Security Domain Score"
      sortMenuItems={{
        data: sortMenuItems,
        tooltip: "View changes since selected time",
      }}
      exportToPdf={true}
      handleExportToPdf={() => handleExportToPdf(false)}
      calculationInfo={{
        label: "How is the score calculated?",
        content: <CalculationInfo />,
      }}
    >
      {isLoadingHeatmapMatrixHistory ? (
        <Box>
          <LoadingIcon size={24} />
        </Box>
      ) : !selectedMatrix || Object.keys(selectedMatrix).length === 0 ? (
        <Box>
          <Typography variant="caption">No data available</Typography>
        </Box>
      ) : (
        <Box ref={contentRef} sx={{ padding: "0px 16px 0px 16px" }}>
          <Typography variant="caption" marginLeft={1}>
            Current posture{" "}
            {baseTimePoint !== LATEST &&
              `vs. ${baseTimePointOptions[baseTimePoint]}`}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              pt: "7px",
              gap: "19px",
              backgroundColor: colors.common_white,
              padding: "8px",
              borderRadius: "10px",
            }}
          >
            <SecurityDomainScoreChart
              domainScores={domainScores}
              trendData={calculateDomainTrends}
              height="240px"
              shouldBeLoaded={!isLoadingHeatmapMatrixHistory}
            />
          </Box>
        </Box>
      )}
    </WidgetWrapper>
  );
});

SecurityDomainScoreWidget.displayName = "SecurityDomainScoreWidget";

export default SecurityDomainScoreWidget;
